// Baselayer Background Checks — "File PDF" modal. Wraps the shared DMS
// FolderPicker so staff choose a destination folder for the rendered report;
// for a deal-linked check the picker defaults to the deal's folder tree.

import { useEffect, useState } from "react";
import { Button } from "@/design-system/components/ui";
import { Modal } from "@/components/ui/Modal";
import { useToast } from "@/components/ui/Toast";
import { ApiError } from "@/api/client";
import { FolderPicker } from "@/components/dms/FolderPicker";
import { useFileBackgroundCheckPdf } from "@/hooks/backgroundChecks/useBackgroundChecks";

interface FilePdfModalProps {
  open: boolean;
  onClose: () => void;
  checkId: string;
  /** When the check is deal-linked, anchor the picker to the deal's tree. */
  dealId: string | null;
}

export function FilePdfModal({ open, onClose, checkId, dealId }: FilePdfModalProps) {
  const { toast } = useToast();
  const file = useFileBackgroundCheckPdf(checkId);
  const [folderId, setFolderId] = useState<string | null>(null);

  useEffect(() => {
    if (open) {
      setFolderId(null);
    }
  }, [open]);

  const handleConfirm = async () => {
    if (!folderId) return;
    try {
      await file.mutateAsync({ folderId });
      toast({ message: "Report filed to the folder.", tone: "success" });
      onClose();
    } catch (err) {
      const message =
        err instanceof ApiError
          ? err.errors[0] ?? err.message
          : err instanceof Error
            ? err.message
            : "Could not file the report.";
      toast({ message, tone: "error" });
    }
  };

  return (
    <Modal
      open={open}
      onClose={onClose}
      title="File report PDF"
      description="Choose a destination folder for the rendered report."
      size="md"
      footer={
        <>
          <Button type="button" variant="secondary" onClick={onClose}>
            Cancel
          </Button>
          <Button
            type="button"
            disabled={!folderId || file.isPending}
            loading={file.isPending}
            onClick={handleConfirm}
            data-testid="file-pdf-confirm"
          >
            File here
          </Button>
        </>
      }
    >
      <FolderPicker
        value={folderId}
        onChange={setFolderId}
        treeSource={
          dealId ? { kind: "entity", entityType: "Deal", entityId: dealId } : null
        }
        search={{ placeholder: "Search folders…", category: "Deal" }}
        browseByDefault={!dealId}
      />
    </Modal>
  );
}
